import React, { useState, useEffect } from "react";
import { LifeBuoy, Phone, MessageSquareWarning, Users, Settings as SettingsIcon } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { getTrustedAdult } from "../lib/kioskConfig";

const HELPLINES = [
  { key: "childline", number: "1098", labelKey: "childline", tone: "primary" },
  { key: "women", number: "181", labelKey: "women_helpline", tone: "primary" },
  { key: "emergency", number: "112", labelKey: "emergency", tone: "destructive" },
  { key: "cyber", number: "1930", labelKey: "cyber_helpline", tone: "accent" },
];

export default function SOSButton() {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [adult, setAdult] = useState(null);

  useEffect(() => {
    if (open) setAdult(getTrustedAdult());
  }, [open]);

  useEffect(() => {
    const openSOS = () => setOpen(true);
    window.addEventListener("sakhi:open-sos", openSOS);
    return () => window.removeEventListener("sakhi:open-sos", openSOS);
  }, []);

  const panicSmsHref = adult?.phone
    ? `sms:${adult.phone}?&body=${encodeURIComponent(t("panic_sms_body"))}`
    : null;

  return (
    <>
      <button
        data-testid="sos-button"
        onClick={() => setOpen(true)}
        aria-label={t("sos")}
        className="fixed z-50 bottom-6 right-6 sm:bottom-8 sm:right-8 rounded-full bg-destructive text-destructive-foreground shadow-[0_14px_40px_rgba(180,40,40,0.45)] w-[74px] h-[74px] flex flex-col items-center justify-center font-bold text-sm active:scale-95 hover:brightness-110 transition-transform focus:outline-none focus:ring-4 focus:ring-destructive/40"
      >
        <LifeBuoy className="w-7 h-7" strokeWidth={2.5} />
        <span className="text-[11px] mt-0.5 tracking-wider">{t("sos")}</span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg bg-white border-0 p-0 overflow-hidden max-h-[90vh] overflow-y-auto">
          <div className="bg-destructive text-destructive-foreground px-6 py-5">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold flex items-center gap-3">
                <LifeBuoy className="w-7 h-7" strokeWidth={2.5} />
                {t("sos_title")}
              </DialogTitle>
              <DialogDescription className="text-white/90 text-base mt-2">
                {t("sos_sub")}
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="p-5 space-y-3" data-testid="sos-helpline-list">
            {/* Panic SMS (only if trusted adult configured) */}
            {panicSmsHref ? (
              <a
                href={panicSmsHref}
                data-testid="sos-panic-sms"
                className="flex items-center justify-between gap-4 rounded-2xl px-5 py-4 border-2 border-destructive bg-destructive text-white transition-colors active:scale-[0.99] hover:brightness-110"
              >
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-white/85">
                    {t("send_panic_sms")}
                  </div>
                  <div className="text-xl font-bold tracking-tight">
                    {adult.name}
                  </div>
                  <div className="text-xs text-white/80 mt-1">{adult.phone}</div>
                </div>
                <div className="w-14 h-14 rounded-full flex items-center justify-center bg-white text-destructive">
                  <MessageSquareWarning className="w-6 h-6" strokeWidth={2.5} />
                </div>
              </a>
            ) : (
              <div
                data-testid="sos-adult-empty"
                className="flex items-center gap-3 rounded-2xl px-4 py-3 bg-secondary/25 border border-border text-sm text-foreground"
              >
                <SettingsIcon className="w-5 h-5 flex-shrink-0" />
                {t("trusted_adult_not_set")}
              </div>
            )}

            {HELPLINES.map((h) => (
              <a
                key={h.key}
                href={`tel:${h.number}`}
                data-testid={`sos-call-${h.key}`}
                className={`flex items-center justify-between gap-4 rounded-2xl px-5 py-4 border-2 transition-colors active:scale-[0.99] ${
                  h.tone === "destructive"
                    ? "border-destructive/40 bg-destructive/5 hover:bg-destructive/10"
                    : h.tone === "accent"
                    ? "border-accent/40 bg-accent/5 hover:bg-accent/10"
                    : "border-primary/40 bg-primary/5 hover:bg-primary/10"
                }`}
              >
                <div>
                  <div className="text-sm font-semibold text-muted-foreground">
                    {t(h.labelKey)}
                  </div>
                  <div className="text-3xl font-bold text-foreground tracking-tight">
                    {h.number}
                  </div>
                </div>
                <div
                  className={`w-14 h-14 rounded-full flex items-center justify-center text-white ${
                    h.tone === "destructive"
                      ? "bg-destructive"
                      : h.tone === "accent"
                      ? "bg-accent"
                      : "bg-primary"
                  }`}
                >
                  <Phone className="w-6 h-6" strokeWidth={2.5} />
                </div>
              </a>
            ))}

            <div className="flex items-start gap-3 mt-2 rounded-2xl bg-secondary/20 border border-border px-4 py-3">
              <Users className="w-5 h-5 text-secondary-foreground flex-shrink-0 mt-0.5" />
              <p className="text-sm text-foreground leading-relaxed">
                {t("sos_talk_adult")}
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
